import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  X,
  Plug,
  CreditCard,
  Image as ImageIcon,
  Filter,
  Target,
  Server,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { handleApi } from "../../utils/handleApi";
import { decodeTokenService } from "../../services/decodeTokenService";
import { builderUrl } from "../../utils/builderUrl";
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
  NotificationItem,
} from "../../services/notifications/notificationsAxios";

const CATEGORY_ICON: Record<string, typeof Bell> = {
  INTEGRATION: Plug,
  PAYMENT: CreditCard,
  CONTENT: ImageIcon,
  FUNNEL: Filter,
  EVENT: Target,
  SYSTEM: Server,
};

const relativeTime = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} d`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { language } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ownerId =
    decodeTokenService.getUserIdFromToken() || import.meta.env.VITE_LOCAL_OWNER_ID;

  const t = (key: string) => dictionary[key]?.[language] || key;

  // Toda la tarjeta navega al arreglo. Destinos reales de la app (no hay pantalla de settings
  // de embudo en el dashboard: envío/píxel se configuran en el builder).
  const resolveAction = (n: NotificationItem): { label: string; run: () => void } | null => {
    if (n.actionType === "OPEN_ORDER" || n.orderId) {
      // actionTarget = hash de la orden → abre la orden EXACTA (drawer), no la lista.
      const to = n.actionTarget ? `/orders?order=${n.actionTarget}` : "/orders";
      return { label: t("notif-action-open-order"), run: () => navigate(to) };
    }
    if (n.category === "FUNNEL") {
      const url = builderUrl(n.websiteId);
      const label =
        n.type === "funnel_missing_shipping"
          ? t("notif-action-configure-shipping")
          : n.type === "funnel_missing_pixel"
          ? t("notif-action-configure-pixel")
          : t("notif-action-open-funnel");
      return { label, run: () => (url ? window.open(url, "_blank") : navigate("/funnels")) };
    }
    if (n.category === "EVENT") {
      // El píxel es por embudo → abre ese embudo en el builder; si no hay embudo, Integraciones.
      const url = builderUrl(n.websiteId);
      return {
        label: t("notif-action-review-pixel"),
        run: () => (url ? window.open(url, "_blank") : navigate("/integrations")),
      };
    }
    if (n.category === "INTEGRATION") {
      return { label: t("notif-action-review-integration"), run: () => navigate("/integrations") };
    }
    return null;
  };

  const loadCount = async () => {
    const { res } = await handleApi(fetchUnreadCount(), false);
    if (res) setUnread(res.count);
  };

  const loadList = async () => {
    setLoading(true);
    const { res } = await handleApi(
      fetchNotifications(filter === "unread" ? { status: "unread" } : undefined),
      false
    );
    if (res) {
      // ?? []: la lista viene de la API (limite de confianza). Un items ausente no puede
      // tumbar el Sidebar entero con un TypeError.
      setItems(res.items ?? []);
      setUnread(res.unreadCount ?? 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCount();
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open, filter]);

  // Tiempo real: el backend escribe unreadCount en Firebase; refrescamos badge (y lista si esta abierta).
  // Todo dinamico + try/catch: si Firebase no esta configurado, la campana sigue por fetch al abrir.
  // (No importar firebase ni useRealtimeEnvironment de forma estatica: arrastrarian firebase.ts al
  // grafo global del Sidebar y tumbarian la app si falta la config de Firebase.)
  useEffect(() => {
    if (!ownerId) return;
    let unsub: (() => void) | undefined;
    (async () => {
      try {
        const [{ ref, onValue }, { database }, { default: config }] = await Promise.all([
          import("firebase/database"),
          import("../../lib/firebase"),
          import("../../config/environment"),
        ]);
        const env = config.environment || "development";
        const metaRef = ref(database, `${env}/owners/${ownerId}/notificationsMeta`);
        unsub = onValue(metaRef, (snap) => {
          const v = snap.val();
          if (v && typeof v.unreadCount === "number") {
            setUnread(v.unreadCount);
            if (open) loadList();
          }
        });
      } catch {
        // Firebase no disponible: la campana funciona con fetch al abrir.
      }
    })();
    return () => {
      if (unsub) unsub();
    };
  }, [ownerId, open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPanel = panelRef.current?.contains(target);
      const inBtn = btnRef.current?.contains(target);
      if (!inPanel && !inBtn) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleOpen = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 8, left: r.left });
    setOpen((o) => !o);
  };

  const onItemClick = async (n: NotificationItem) => {
    if (!n.read) {
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read: true } : it)));
      setUnread((u) => Math.max(0, u - 1));
      handleApi(markNotificationRead(n.id), false);
    }
    const action = resolveAction(n);
    if (action) {
      setOpen(false);
      action.run();
    }
  };

  const onDismiss = async (e: React.MouseEvent, n: NotificationItem) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((it) => it.id !== n.id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
    handleApi(dismissNotification(n.id), false);
  };

  const onMarkAll = async () => {
    setItems((prev) => prev.map((it) => ({ ...it, read: true })));
    setUnread(0);
    handleApi(markAllNotificationsRead(), false);
  };

  return (
    <>
      <button
        ref={btnRef}
        aria-label={t("notif-title")}
        onClick={(e) => {
          e.stopPropagation();
          toggleOpen();
        }}
        className="relative flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
      >
        <Bell size={19} className="text-fluxi" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
            className="w-[360px] bg-white rounded-2xl border border-[#E9EDF7] shadow-[0_8px_24px_rgba(112,144,176,0.20)] overflow-hidden cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="px-4 pt-4 pb-3 border-b border-gray-100">
            <div className="flex items-center">
              <span className="font-semibold text-[15px] text-gray-800">
                {t("notif-title")}
              </span>
              {unread > 0 && (
                <button
                  onClick={onMarkAll}
                  className="ml-auto text-xs text-fluxi hover:underline"
                >
                  {t("notif-mark-all-read")}
                </button>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              {(["all", "unread"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    filter === f
                      ? "bg-fluxi text-white border-fluxi"
                      : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {f === "all" ? t("notif-filter-all") : t("notif-filter-unread")}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="py-10 text-center text-sm text-gray-400">…</div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">{t("notif-empty")}</div>
            ) : (
              <div className="flex flex-col">
                {items.map((n) => {
                  const Icon = CATEGORY_ICON[n.category] || Bell;
                  const action = resolveAction(n);
                  const critical = n.severity === "CRITICAL" && n.status !== "RESOLVED";
                  return (
                    <div
                      key={n.id}
                      onClick={() => onItemClick(n)}
                      className={`group flex gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${
                        !n.read ? "bg-[#F7F9FF]" : ""
                      }`}
                    >
                      <div className="shrink-0 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                        {critical ? (
                          <AlertTriangle size={17} className="text-red-500" />
                        ) : (
                          <Icon size={17} className="text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <span
                            className={`text-[13px] leading-snug ${
                              !n.read ? "font-semibold text-gray-800" : "text-gray-600"
                            }`}
                          >
                            {n.title}
                            {n.occurrences > 1 && (
                              <span className="ml-1 text-[11px] text-gray-400">
                                ×{n.occurrences}
                              </span>
                            )}
                          </span>
                          <button
                            aria-label="dismiss"
                            onClick={(e) => onDismiss(e, n)}
                            className="ml-auto opacity-0 group-hover:opacity-100 text-gray-300 hover:text-gray-500"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        {n.body && (
                          <p className="text-[12px] text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[11px] text-gray-400">
                            {relativeTime(n.lastSeenAt)}
                          </span>
                          {action && (
                            <span className="ml-auto text-[12px] text-fluxi font-medium">
                              {action.label}
                            </span>
                          )}
                        </div>
                      </div>
                      {!n.read && (
                        <span className="shrink-0 self-center w-2 h-2 rounded-full bg-fluxi" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
          document.body
        )}
    </>
  );
};

export default NotificationBell;
